/**
 * Customer model for managing customer information
 */
import { v4 as uuidv4 } from 'uuid';

class Customer {
  /**
   * Constructor
   * @param {Object} customerData - Customer data
   */
  constructor(customerData = {}) {
    this.id = customerData.id || uuidv4();
    this.name = customerData.name || '';
    this.phoneNumber = customerData.phoneNumber || customerData.phone || '';
    this.email = customerData.email || '';
    this.birthdate = customerData.birthdate || null;
    this.age = customerData.age || null;
    this.gender = customerData.gender || ''; // '男', '女', or other
    this.address = customerData.address || '';
    this.wechatId = customerData.wechatId || '';
    this.emergencyContact = customerData.emergencyContact || '';
    this.referredBy = customerData.referredBy || '';
    this.firstVisitDate = customerData.firstVisitDate || null;
    this.lastVisitDate = customerData.lastVisitDate || null;
    this.visitCount = customerData.visitCount || 0;
    this.medicalHistory = customerData.medicalHistory || [];
    this.allergies = customerData.allergies || [];
    this.notes = customerData.notes || '';
    this.tags = customerData.tags || [];
    this.createdAt = customerData.createdAt || new Date().toISOString();
    this.updatedAt = customerData.updatedAt || new Date().toISOString();
    this.isActive = customerData.isActive !== undefined ? customerData.isActive : true;
    this.avatar = customerData.avatar || null;

    // New fields for detailed health profile
    this.customerType = customerData.customerType || 'new'; // 'new' or 'returning'
    this.height = customerData.height || null; // in cm
    this.weight = customerData.weight || null; // in kg
    this.lifestyle = customerData.lifestyle || { diet: '', sleep: '', exercise: '' };
    this.familyHistory = customerData.familyHistory || [];
    this.currentSymptoms = customerData.currentSymptoms || [];
    this.intendedProject = customerData.intendedProject || '';
    this.intendedPractitioner = customerData.intendedPractitioner || '';
    this.specialNeeds = customerData.specialNeeds || '';
    this.healthProfile = customerData.healthProfile || {};

    // Age group: 'youth', 'adult', 'senior'
    this.ageGroup = customerData.ageGroup || this._calculateAgeGroup();
  }

  /**
   * Update customer data
   * @param {Object} customerData - Customer data to update
   * @returns {Customer} Updated customer instance
   */
  update(customerData) {
    // Update fields that are provided
    Object.keys(customerData).forEach(key => {
      if (customerData[key] !== undefined && key !== 'id') {
        this[key] = customerData[key];
      }
    });

    // Recalculate age group if age or birthdate is updated
    if (customerData.age !== undefined || customerData.birthdate !== undefined) {
      this.ageGroup = this._calculateAgeGroup();
    }

    // Update the updatedAt timestamp
    this.updatedAt = new Date().toISOString();

    return this;
  }

  /**
   * Record a customer visit
   * @param {Date|string} [visitDate] - Visit date
   * @returns {Customer} Updated customer instance
   */
  recordVisit(visitDate = new Date()) {
    const visit = visitDate instanceof Date ? visitDate : new Date(visitDate);

    if (!this.firstVisitDate) {
      this.firstVisitDate = visit.toISOString();
    }

    this.lastVisitDate = visit.toISOString();
    this.visitCount += 1;
    this.updatedAt = new Date().toISOString();

    return this;
  }

  /**
   * Add a medical condition to history
   * @param {string} condition - Medical condition
   * @param {string} details - Condition details
   * @param {Date|string} [diagnosisDate=null] - Date of diagnosis
   * @returns {Customer} Updated customer instance
   */
  addMedicalCondition(condition, details, diagnosisDate = null) {
    const date = diagnosisDate ?
      (diagnosisDate instanceof Date ? diagnosisDate.toISOString() : diagnosisDate) :
      new Date().toISOString();

    this.medicalHistory.push({
      condition,
      details,
      diagnosisDate: date,
      recordedAt: new Date().toISOString()
    });

    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Add an allergy
   * @param {string} allergen - Allergen name
   * @param {string} [reaction=''] - Allergic reaction details
   * @param {string} [severity='moderate'] - Severity level
   * @returns {Customer} Updated customer instance
   */
  addAllergy(allergen, reaction = '', severity = 'moderate') {
    this.allergies.push({
      allergen,
      reaction,
      severity,
      recordedAt: new Date().toISOString()
    });

    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Add tag to customer
   * @param {string} tag - Tag to add
   * @returns {Customer} Updated customer instance
   */
  addTag(tag) {
    if (!this.tags.includes(tag)) {
      this.tags.push(tag);
      this.updatedAt = new Date().toISOString();
    }
    return this;
  }

  /**
   * Remove tag from customer
   * @param {string} tag - Tag to remove
   * @returns {Customer} Updated customer instance
   */
  removeTag(tag) {
    const index = this.tags.indexOf(tag);
    if (index !== -1) {
      this.tags.splice(index, 1);
      this.updatedAt = new Date().toISOString();
    }
    return this;
  }

  /**
   * Get customer age
   * @returns {number|null} Age in years or null if birthdate not set
   */
  getAge() {
    if (!this.birthdate) return null;

    const birthDate = new Date(this.birthdate);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Get time since last visit
   * @returns {number|null} Days since last visit or null if no visits
   */
  getDaysSinceLastVisit() {
    if (!this.lastVisitDate) return null;

    const lastVisit = new Date(this.lastVisitDate);
    const today = new Date();
    const diffTime = today - lastVisit;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Activate customer
   * @returns {Customer} Updated customer instance
   */
  activate() {
    this.isActive = true;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Deactivate customer
   * @returns {Customer} Updated customer instance
   */
  deactivate() {
    this.isActive = false;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Calculate age group based on age or birthdate
   * @returns {string} 'youth' | 'adult' | 'senior'
   */
  _calculateAgeGroup() {
    let age = this.age;
    if (!age && this.birthdate) {
      age = this.getAge();
    }
    if (age === null || age === undefined) return '';
    if (age < 18) return 'youth';
    if (age < 60) return 'adult';
    return 'senior';
  }

  /**
   * Convert to JSON for storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      phoneNumber: this.phoneNumber,
      phone: this.phoneNumber, // Backward compatibility
      email: this.email,
      birthdate: this.birthdate,
      age: this.age,
      gender: this.gender,
      address: this.address,
      wechatId: this.wechatId,
      emergencyContact: this.emergencyContact,
      referredBy: this.referredBy,
      firstVisitDate: this.firstVisitDate,
      lastVisitDate: this.lastVisitDate,
      visitCount: this.visitCount,
      medicalHistory: this.medicalHistory,
      allergies: this.allergies,
      notes: this.notes,
      tags: this.tags,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      isActive: this.isActive,
      avatar: this.avatar,
      // New fields
      customerType: this.customerType,
      height: this.height,
      weight: this.weight,
      lifestyle: this.lifestyle,
      familyHistory: this.familyHistory,
      currentSymptoms: this.currentSymptoms,
      intendedProject: this.intendedProject,
      intendedPractitioner: this.intendedPractitioner,
      specialNeeds: this.specialNeeds,
      healthProfile: this.healthProfile,
      ageGroup: this.ageGroup
    };
  }

  /**
   * Create customer from JSON data
   * @param {Object} json - JSON data
   * @returns {Customer} Customer instance
   * @static
   */
  static fromJSON(json) {
    return new Customer(json);
  }
}

export default Customer;